import { FormikProvider, useFormik } from 'formik';
import { CompanyUpdateInput } from 'lib/generated/graphql';
import FormProfilePicture from 'src/screens/company/components/CompleteProfile/FormProfilePicture';
import { Button } from 'src/shared-components';
import { InputField, Select } from 'src/shared-components/Form';
import * as Yup from 'yup';

const DEFAULT_USER_AVATAR = process.env.DEFAULT_USER_AVATAR || '';

const stepOneOptions = {
  fundingStage: [
    {
      value: '<$50k',
      label: '<$50k',
    },
    {
      value: '$50-$250k',
      label: '$50-$250k',
    },
    {
      value: '$250k-$2M',
      label: '$250k-$2M',
    },
    {
      value: '$2M+',
      label: '$2M+',
    },
  ],
};
const CustomerCompanyComplete = ({
  onFormSubmitted,
  buttonText = 'Next',
}: {
  onFormSubmitted: (companyUpdateInput: CompanyUpdateInput) => void;
  buttonText?: string;
}) => {
  const form = useFormik({
    initialValues: {
      oneLiner: '',
      website: '',
      fundingStage: '',
      photoUrl: '',
    },
    validationSchema: Yup.object({
      oneLiner: Yup.string().required('Tell us a little about your company, it helps make the process smoother'),
      fundingStage: Yup.string().required("We need to know your company's funding stage"),
      website: Yup.string().matches(
        /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
        'Enter a correct url',
      ),
      photoUrl: Yup.string(),
    }),
    onSubmit: async ({ oneLiner, website, fundingStage, photoUrl }) => {
      try {
        onFormSubmitted({
          pitch: oneLiner,
          socialLinks: {
            create: [
              {
                platformName: 'Website',
                link: website ? website : '',
              },
            ],
          },
          fundingStage,
          photoUrl,
        });
      } catch (error) {
        console.error(error);
      }
    },
  });

  const { isValid, handleSubmit, setFieldValue, values } = form;

  return (
    <FormikProvider value={form}>
      <div className="flex flex-col gap-y-4 p-3">
        <div className="mb-6">
          <FormProfilePicture
            photoUrl={values.photoUrl || DEFAULT_USER_AVATAR}
            onFileUploaded={url => setFieldValue('photoUrl', url)}
            onRemovePhotoUrl={() => setFieldValue('photoUrl', null)}
            photoRoundness="md"
            flexStyle
          />
        </div>
        <div className="col-span-6 sm:col-span-6">
          <InputField
            name="oneLiner"
            dataTestId="oneLiner"
            aria-label="oneLiner"
            autoComplete="off"
            label="one-liner"
            placeholder="Short pitch on what your company does"
            required
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <InputField
            name="website"
            dataTestId="website"
            aria-label="website"
            autoComplete="off"
            label="website"
            placeholder="www.website.com"
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Select
            data-testid="fundingStage"
            options={stepOneOptions.fundingStage}
            label="funding stage"
            name="fundingStage"
            placeholder="Select one..."
            required
          />
        </div>
        <Button
          onClick={() => handleSubmit()}
          size="xl"
          className="bg-[#151515] mt-2"
          roundness="md"
          color="black"
          fullWidth
          disabled={!isValid}
        >
          {buttonText}
        </Button>
      </div>
    </FormikProvider>
  );
};

export default CustomerCompanyComplete;
